import React, { useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import { collectContentTextNodes, getContentText } from '../utils/contentTextNodes';

/**
 * Wraps content and applies saved Ask Graspd highlights.
 * Uses MutationObserver to detect when content is fully rendered,
 * handling React.lazy, Suspense, and re-renders gracefully.
 *
 * @param {{
 *   children: React.ReactNode,
 *   history: Array<{ id: string, selectedText: string, startOffset: number, endOffset: number, questionType: string, customQuestion?: string, answer: string, createdAt: string }>,
 *   containerRef: React.RefObject<HTMLElement>
 * }} props
 */
export function SavedHighlightsWrapper({ children, history, containerRef }) {
  // activeHighlight can be a single item or an array of items (for overlaps)
  const [activeHighlight, setActiveHighlight] = useState(null);
  const [popupPosition, setPopupPosition] = useState({ top: 0, left: 0 });
  const observerRef = useRef(null);
  const debounceTimerRef = useRef(null);

  const handleHighlightClick = useCallback((itemOrItems, event) => {
    const rect = event.target.getBoundingClientRect();
    setPopupPosition({
      top: rect.bottom + window.scrollY + 8,
      left: rect.left + rect.width / 2,
    });
    // Normalize to always be an array for consistent handling
    setActiveHighlight(Array.isArray(itemOrItems) ? itemOrItems : [itemOrItems]);
  }, []);

  const closePopup = useCallback(() => {
    setActiveHighlight(null);
  }, []);

  // Apply highlights to the container
  const tryApplyHighlights = useCallback(() => {
    const container = containerRef?.current;
    if (!container || !history || history.length === 0) {
      return;
    }

    // Check if content looks like actual content (not a loading fallback)
    // Fallbacks are typically short text like "Loading..."
    const textContent = container.textContent || '';
    if (textContent.length < 50) {
      // Content is likely a loading fallback, wait for real content
      return;
    }

    // Validate + re-anchor every history item against the container's
    // current content text before touching the DOM. `resolved` only ever
    // contains items we've decided are safe to render (see
    // resolveHighlightRange) - this is also what the "already applied"
    // check below compares against, so a permanently-unresolvable item
    // (selectedText genuinely gone from the lesson) can never cause an
    // apply/remove loop.
    const containerText = getContentText(container);
    const resolved = [];
    for (const item of history) {
      const range = resolveHighlightRange(item, containerText);
      if (range) {
        resolved.push({ ...item, startOffset: range.startOffset, endOffset: range.endOffset });
      }
    }

    // Check if highlights already exist
    const existingHighlights = container.querySelectorAll('.graspd-saved-highlight');
    if (existingHighlights.length > 0) {
      // Collect all IDs from existing highlights (including overlapping segments)
      const existingIds = new Set();
      for (const el of existingHighlights) {
        // Check data-graspd-ids first (for segments with multiple highlights)
        const idsJson = el.getAttribute('data-graspd-ids');
        if (idsJson) {
          try {
            const ids = JSON.parse(idsJson);
            ids.forEach(id => existingIds.add(id));
          } catch {
            // Fallback to single ID
            const id = el.getAttribute('data-graspd-id');
            if (id) existingIds.add(id);
          }
        } else {
          const id = el.getAttribute('data-graspd-id');
          if (id) existingIds.add(id);
        }
      }

      // If exactly the resolvable set is already highlighted, we're done
      if (resolved.length === existingIds.size && resolved.every(h => existingIds.has(h.id))) {
        return;
      }

      // Otherwise, remove and re-apply. removeHighlights only changes node
      // boundaries (span -> text node, then normalize), never the
      // underlying character content, so offsets computed against
      // containerText above remain valid afterward.
      removeHighlights(container);
    }

    if (resolved.length === 0) return;

    // Apply highlights using a fresh walk (node boundaries may have just
    // changed via removeHighlights above).
    const textNodes = collectContentTextNodes(container);
    applyHighlightsToNodes(textNodes, resolved, handleHighlightClick);
  }, [containerRef, history, handleHighlightClick]);

  // Main effect: set up MutationObserver to detect content changes
  useEffect(() => {
    const container = containerRef?.current;
    if (!container || !history || history.length === 0) {
      return;
    }

    // Debounced apply function - waits for content to stabilize
    const debouncedApply = () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      debounceTimerRef.current = setTimeout(() => {
        tryApplyHighlights();
      }, 100);
    };

    // Initial attempt
    debouncedApply();

    // Watch for content changes (handles Suspense resolution, React re-renders)
    observerRef.current = new MutationObserver((mutations) => {
      // Check if our highlights still exist
      const hasHighlights = container.querySelector('.graspd-saved-highlight');

      // If highlights are completely gone, re-apply
      if (!hasHighlights) {
        debouncedApply();
        return;
      }

      // Check if any mutation is NOT related to our highlight operations
      // Our operations add: highlight spans, and text nodes (siblings of highlights)
      let shouldReapply = false;

      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          // Check added nodes - ignore text nodes and highlight spans
          for (const node of mutation.addedNodes) {
            // Text nodes are fine (we create them when splitting)
            if (node.nodeType === Node.TEXT_NODE) continue;
            // Mermaid rendering its <svg> (and every <g>/<foreignObject>
            // inside it) is not a lesson-content change - ignore the whole
            // diagram region so a diagram render never triggers a reapply
            // (which would re-walk and could corrupt the svg).
            if (isInsideDiagram(node)) continue;
            // Highlight spans are fine
            if (node.classList?.contains('graspd-saved-highlight')) continue;
            // Any other element means external content change
            if (node.nodeType === Node.ELEMENT_NODE) {
              shouldReapply = true;
              break;
            }
          }
          if (shouldReapply) break;

          // Check removed nodes - if a highlight was removed externally, re-apply
          for (const node of mutation.removedNodes) {
            // Diagram churn (mermaid re-rendering its svg) is not a highlight
            // removal - ignore it for the same reason as added nodes above.
            if (isInsideDiagram(node)) continue;
            if (node.classList?.contains('graspd-saved-highlight')) {
              // A highlight was removed - check if we still have all IDs
              shouldReapply = true;
              break;
            }
          }
          if (shouldReapply) break;
        }
      }

      if (shouldReapply) {
        debouncedApply();
      }
    });

    observerRef.current.observe(container, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
      // Clean up highlights on unmount
      removeHighlights(container);
    };
  }, [containerRef, history, tryApplyHighlights]);

  // Handle escape key to close popup
  useEffect(() => {
    if (!activeHighlight) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        closePopup();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [activeHighlight, closePopup]);

  // Handle click outside to close popup
  useEffect(() => {
    if (!activeHighlight) return;

    const handleClickOutside = (e) => {
      if (e.target.closest('.graspd-saved-highlight')) {
        return;
      }
      if (e.target.closest('.graspd-saved-response-popover')) {
        return;
      }
      closePopup();
    };

    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 100);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [activeHighlight, closePopup]);

  return (
    <>
      {children}
      {activeHighlight && activeHighlight.length > 0 && createPortal(
        <SavedHighlightPopup
          items={activeHighlight}
          position={popupPosition}
          onClose={closePopup}
        />,
        document.body
      )}
    </>
  );
}

/**
 * Popup to show saved Ask Graspd response(s)
 * Supports multiple overlapping highlights with navigation
 */
function SavedHighlightPopup({ items, position, onClose }) {
  const [activeIndex, setActiveIndex] = useState(0);
  const item = items[activeIndex];
  const hasMultiple = items.length > 1;

  const QUESTION_LABELS = {
    EXPLAIN: 'Explain this in simpler terms',
    EXAMPLE: 'Give me a concrete example',
    DEEPER: 'Go deeper into this concept',
    WHY: 'Why is this important?',
    HOW: 'How does this work in practice?',
    CUSTOM: item.customQuestion || 'Custom question',
  };

  const goToPrev = () => setActiveIndex(i => Math.max(0, i - 1));
  const goToNext = () => setActiveIndex(i => Math.min(items.length - 1, i + 1));

  return (
    <AnimatePresence>
      <motion.div
        className="graspd-saved-response-popover"
        style={{
          position: 'fixed',
          top: Math.min(position.top, window.innerHeight - 350),
          left: Math.max(16, Math.min(position.left, window.innerWidth - 200)),
          transform: 'translateX(-50%)',
          zIndex: 10000,
        }}
        initial={{ opacity: 0, y: -10, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: -10, scale: 0.95 }}
        transition={{ duration: 0.2, ease: 'easeOut' }}
        role="dialog"
        aria-label="Saved Ask Graspd response"
      >
        <div className="graspd-saved-response-header">
          <div className="graspd-saved-response-title">
            <span className="graspd-saved-response-icon">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707" />
                <circle cx="12" cy="12" r="4" />
              </svg>
            </span>
            <span>Previous Question{hasMultiple ? 's' : ''}</span>
          </div>
          <button className="graspd-saved-response-close" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>

        {/* Navigation for multiple overlapping highlights */}
        {hasMultiple && (
          <div className="graspd-saved-response-nav">
            <button
              className="graspd-saved-response-nav-btn"
              onClick={goToPrev}
              disabled={activeIndex === 0}
              aria-label="Previous question"
            >
              ‹
            </button>
            <span className="graspd-saved-response-nav-indicator">
              {activeIndex + 1} of {items.length}
            </span>
            <button
              className="graspd-saved-response-nav-btn"
              onClick={goToNext}
              disabled={activeIndex === items.length - 1}
              aria-label="Next question"
            >
              ›
            </button>
          </div>
        )}

        <div className="graspd-saved-response-question">
          "{QUESTION_LABELS[item.questionType]}"
        </div>

        <div className="graspd-saved-response-selected-text">
          <span className="graspd-saved-response-label">About:</span> "{item.selectedText.slice(0, 100)}{item.selectedText.length > 100 ? '...' : ''}"
        </div>

        <div className="graspd-saved-response-content">
          <ReactMarkdown>{item.answer}</ReactMarkdown>
        </div>

        <div className="graspd-saved-response-footer">
          <span className="graspd-saved-response-date">
            {new Date(item.createdAt).toLocaleDateString(undefined, {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            })}
          </span>
          <button className="graspd-saved-response-close-btn" onClick={onClose}>
            Close
          </button>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}

/**
 * True when a mutated node is, or lives inside, a rendered diagram
 * (`svg` / `.lesson-diagram` / `.mermaid`). Mermaid renders its diagram in
 * the same DOM subtree the highlight machinery watches; treating its svg
 * insertions/removals as content changes would spuriously re-apply
 * highlights (and re-walk into the svg). Kept in sync with the diagram
 * exclusions in contentTextNodes.js so both systems ignore the same region.
 */
function isInsideDiagram(node) {
  if (!node) return false;
  const el = node.nodeType === Node.ELEMENT_NODE ? node : node.parentElement;
  if (!el || typeof el.closest !== 'function') return false;
  return !!el.closest('svg, .lesson-diagram, .mermaid');
}

/**
 * Create a highlight span element with event handlers
 */
function createHighlightSpan(items, text, onClickHandler) {
  const highlightSpan = document.createElement('span');
  highlightSpan.className = 'graspd-saved-highlight';
  highlightSpan.textContent = text;

  // Store all associated highlight IDs
  const itemIds = items.map(item => item.id);
  highlightSpan.setAttribute('data-graspd-ids', JSON.stringify(itemIds));
  highlightSpan.setAttribute('data-graspd-id', itemIds[0]);
  highlightSpan.setAttribute('role', 'button');
  highlightSpan.setAttribute('tabindex', '0');

  if (items.length > 1) {
    highlightSpan.classList.add('graspd-saved-highlight--overlap');
    highlightSpan.setAttribute('aria-label', `View ${items.length} previous Ask Graspd responses`);
  } else {
    highlightSpan.setAttribute('aria-label', 'View previous Ask Graspd response');
  }

  // Track mousedown position to distinguish click from selection drag
  let mouseDownPos = null;
  let wasDragging = false;
  const DRAG_THRESHOLD = 5;

  highlightSpan.addEventListener('mousedown', (e) => {
    mouseDownPos = { x: e.clientX, y: e.clientY };
    wasDragging = false;
  });

  highlightSpan.addEventListener('mouseup', (e) => {
    if (mouseDownPos) {
      const dx = Math.abs(e.clientX - mouseDownPos.x);
      const dy = Math.abs(e.clientY - mouseDownPos.y);
      wasDragging = dx > DRAG_THRESHOLD || dy > DRAG_THRESHOLD;
    }
  });

  highlightSpan.addEventListener('click', (e) => {
    if (wasDragging) {
      mouseDownPos = null;
      wasDragging = false;
      return;
    }
    mouseDownPos = null;
    wasDragging = false;

    const selection = window.getSelection();
    if (selection && selection.toString().trim().length > 0) {
      return;
    }

    e.stopPropagation();
    onClickHandler(items.length === 1 ? items[0] : items, e);
  });

  highlightSpan.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClickHandler(items.length === 1 ? items[0] : items, e);
    }
  });

  return highlightSpan;
}

/**
 * Determine the effective [startOffset, endOffset) to render for a single
 * saved highlight, validating the stored offsets against the persisted
 * `selectedText` and re-anchoring when they've drifted (e.g. because CSS
 * that used to live inside the content container - see contentTextNodes.js
 * - was edited, which used to silently shift every downstream offset).
 *
 * This is display-only: it never writes corrected offsets back anywhere.
 *
 * Returns null when the highlight should not be rendered at all: the
 * record is malformed, or `selectedText` no longer appears anywhere in the
 * content (the lesson itself changed) and guessing would be worse than
 * showing nothing.
 */
export function resolveHighlightRange(item, containerText) {
  const { startOffset, endOffset, selectedText } = item || {};

  if (
    typeof startOffset !== 'number' ||
    typeof endOffset !== 'number' ||
    !Number.isFinite(startOffset) ||
    !Number.isFinite(endOffset) ||
    endOffset <= startOffset
  ) {
    return null;
  }

  const expected = (selectedText || '').trim();

  // Older records (or a genuinely blank selection) have nothing to
  // validate against. Fall back to the stored offsets as-is - we can't
  // self-heal without selectedText, but we also shouldn't hide a
  // highlight that may well still be correct, or crash.
  if (!expected) {
    return { startOffset, endOffset };
  }

  const actual = containerText.slice(startOffset, endOffset);
  if (actual.trim() === expected) {
    // Offsets still point at the saved text - nothing to fix. Taking this
    // fast path (no search) keeps already-correct highlights landing in
    // exactly the same place as before.
    return { startOffset, endOffset };
  }

  // Offsets are stale. Search every occurrence of the saved text and
  // re-anchor to whichever is closest to the stale offset - when content
  // shifts uniformly (the CSS-edit case) the intended occurrence is
  // almost always the nearest one. Single linear pass over containerText.
  let searchFrom = 0;
  let bestIndex = -1;
  let bestDistance = Infinity;
  for (;;) {
    const idx = containerText.indexOf(expected, searchFrom);
    if (idx === -1) break;
    const distance = Math.abs(idx - startOffset);
    if (distance < bestDistance) {
      bestDistance = distance;
      bestIndex = idx;
    }
    searchFrom = idx + 1;
  }

  if (bestIndex === -1) {
    // The lesson content changed and the text is genuinely gone. Render
    // nothing rather than guessing at a wrong location.
    return null;
  }

  return { startOffset: bestIndex, endOffset: bestIndex + expected.length };
}

/**
 * Apply highlight spans to the DOM at (already-resolved) offsets.
 * Handles overlapping highlights by processing each text node once.
 *
 * Algorithm:
 * 1. `textNodes` is a pre-collected list of content text nodes with their
 *    global character offsets (see collectContentTextNodes)
 * 2. For each text node, find all highlight boundaries within it
 * 3. Split the text node into segments (plain text and highlighted spans)
 * 4. Replace text nodes in reverse order to preserve references
 *
 * @param {Array<{node: Text, start: number, end: number}>} textNodes
 * @param {Array} resolvedHistory - history items with validated/re-anchored startOffset/endOffset
 */
function applyHighlightsToNodes(textNodes, resolvedHistory, onClickHandler) {
  if (!textNodes || textNodes.length === 0 || !resolvedHistory || resolvedHistory.length === 0) return;

  // Build a list of replacement operations (we'll execute in reverse)
  const replacements = [];

  for (const { node, start: nodeStart, end: nodeEnd } of textNodes) {
    const text = node.textContent || '';
    if (!text) continue;

    // Find all boundary points within this text node from all highlights
    const boundarySet = new Set([0, text.length]);

    for (const item of resolvedHistory) {
      const { startOffset, endOffset } = item;
      // Check if this highlight intersects with this text node
      if (endOffset > nodeStart && startOffset < nodeEnd) {
        // Convert to local offsets within this text node
        const localStart = Math.max(0, startOffset - nodeStart);
        const localEnd = Math.min(text.length, endOffset - nodeStart);
        boundarySet.add(localStart);
        boundarySet.add(localEnd);
      }
    }

    const boundaries = [...boundarySet].sort((a, b) => a - b);

    // If only 0 and text.length, no highlights intersect this node
    if (boundaries.length <= 2) {
      // Check if entire node is inside any highlight
      const activeItems = resolvedHistory.filter(item =>
        item.startOffset <= nodeStart && item.endOffset >= nodeEnd
      );
      if (activeItems.length > 0) {
        // Entire node is highlighted
        replacements.push({
          node,
          segments: [{ text, items: activeItems }],
        });
      }
      continue;
    }

    // Build segments between boundaries
    const segments = [];
    for (let i = 0; i < boundaries.length - 1; i++) {
      const segStart = boundaries[i];
      const segEnd = boundaries[i + 1];
      const segText = text.slice(segStart, segEnd);

      if (!segText) continue;

      // Find which highlights cover this segment (using global offsets)
      const globalSegStart = nodeStart + segStart;
      const globalSegEnd = nodeStart + segEnd;

      const activeItems = resolvedHistory.filter(item =>
        item.startOffset <= globalSegStart && item.endOffset >= globalSegEnd
      );

      segments.push({
        text: segText,
        items: activeItems,
      });
    }

    // Only add replacement if there's at least one highlighted segment
    const hasHighlight = segments.some(seg => seg.items.length > 0);
    if (hasHighlight) {
      replacements.push({ node, segments });
    }
  }

  // Apply replacements in reverse order to preserve earlier node references
  for (let i = replacements.length - 1; i >= 0; i--) {
    const { node, segments } = replacements[i];
    const parent = node.parentNode;
    if (!parent) continue;

    const fragment = document.createDocumentFragment();

    for (const seg of segments) {
      if (seg.items.length > 0) {
        // This segment is highlighted
        fragment.appendChild(createHighlightSpan(seg.items, seg.text, onClickHandler));
      } else {
        // Plain text
        fragment.appendChild(document.createTextNode(seg.text));
      }
    }

    parent.replaceChild(fragment, node);
  }
}

/**
 * Remove all highlight spans from the container
 */
function removeHighlights(container) {
  if (!container) return;

  const highlights = container.querySelectorAll('.graspd-saved-highlight');
  highlights.forEach((span) => {
    const parent = span.parentNode;
    if (!parent) return;

    // Replace span with its text content
    const textNode = document.createTextNode(span.textContent || '');
    parent.replaceChild(textNode, span);
  });

  // Normalize text nodes (merge adjacent text nodes)
  container.normalize();
}

export default SavedHighlightsWrapper;
